/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	memo,
	useState,
	type ForwardRefExoticComponent,
	type MemoExoticComponent,
	type RefAttributes,
} from 'react';

import { jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import { TR, type TRProps } from '../../ui/primitives/tr';
import { type RowSpacing } from '../charlie-table.types';

const styles = cssMap({
	bodyRow: {
		// minHeight: '45px', this causes issues with Safari; leaving it commented so folks don't revert
		position: 'relative',
		border: 'none',
		borderImageWidth: 0,
		borderSpacing: 0,
		display: 'flex',
		width: '100%',
		alignItems: 'center',
	},

	compactRow: {
		// minHeight: '36px',  this causes issues with Safari; leaving it commented so folks don't revert
	},

	backgroundDefault: {
		backgroundColor: token('elevation.surface'),
	},

	backgroundHovered: {
		backgroundColor: token('elevation.surface.raised.hovered'),
	},
});

export interface RowProps extends TRProps {
	spacing?: RowSpacing;
}

/**
 * __Row__
 *
 * A table row.
 *
 * - [Examples](https://atlassian.design/components/table/examples)
 */

export const Row: MemoExoticComponent<
	ForwardRefExoticComponent<Omit<RowProps, 'ref'> & RefAttributes<HTMLTableRowElement>>
> = memo(
	forwardRef<HTMLTableRowElement, RowProps>(
		({ children, testId, spacing, xcss, isSelected, ...props }, ref) => {
			const [isHovered, setIsHovered] = useState(false);

			const handleMouseEnter = (event: React.MouseEvent<HTMLTableRowElement>) => {
				setIsHovered(true);
				fg('charly-table-with-row-table-handler') && props.onMouseEnter?.(event);
			};

			const handleMouseLeave = (event: React.MouseEvent<HTMLTableRowElement>) => {
				setIsHovered(false);
				fg('charly-table-with-row-table-handler') && props.onMouseLeave?.(event);
			};

			return (
				<TR
					ref={ref}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					isSelected={isSelected}
					testId={testId}
					xcss={cx(
						styles.bodyRow,
						spacing === 'compact' && styles.compactRow,
						isHovered ? styles.backgroundHovered : styles.backgroundDefault,
						xcss,
					)}
					{...props}
				>
					{children}
				</TR>
			);
		},
	),
);
